import React from 'react';
import { ExternalLink, Edit2, Trash2 } from 'lucide-react';

function PortfolioPage({ darkMode, portfolioItems, editPortfolio, deletePortfolio, setActivePage, isAdmin }) {
  const themeClasses = {
    background: darkMode ? 'bg-gray-900' : 'bg-gray-50',
    card: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-gray-100' : 'text-gray-800',
    subtext: darkMode ? 'text-gray-400' : 'text-gray-600',
    accent: darkMode ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700' : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600',
    projectBtn: darkMode ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700' : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600',
    tag: darkMode ? 'bg-indigo-900/70 text-indigo-300 border border-indigo-700' : 'bg-indigo-100 text-indigo-800 border border-indigo-200',
    editBtn: darkMode ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700' : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600',
    deleteBtn: darkMode ? 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700' : 'bg-gradient-to-r from-rose-500 to-red-500 hover:from-rose-600 hover:to-red-600',
    shadow: 'shadow-xl hover:shadow-2xl transition-all duration-300'
  };
  const getImageSrc = (item) => {
    return item.imageUrl || '/api/placeholder/600/400';
  };
  

  return (
    <div className={`${themeClasses.background} min-h-screen py-12 px-4 sm:px-6`}>
      <div className="max-w-7xl mx-auto">
        <h2 className={`text-4xl md:text-5xl font-extrabold mb-12 ${themeClasses.text} tracking-tight fade-in-down relative`}>
          <span className="relative inline-block pb-3">
            Portfolio Projects
            <span className="absolute bottom-0 left-0 w-full h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"></span>
          </span>
        </h2>
        
        {portfolioItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioItems.map((item, index) => (
              <div
                key={item.id}
                className={`rounded-2xl overflow-hidden ${themeClasses.card} ${themeClasses.shadow} border hover-lift group fade-in-up ${darkMode ? 'border-gray-700/50 hover:border-purple-500/50' : 'border-gray-100 hover:border-indigo-200'}`}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <div className="relative overflow-hidden aspect-video bg-gradient-to-br from-indigo-100 to-purple-100">
                  <img
                    src={getImageSrc(item)}
                    alt={item.title}
                    className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110 group-hover:rotate-1"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/api/placeholder/600/400';
                    }}
                  />
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-95 transition-all duration-300 flex items-center justify-center backdrop-blur-sm ${
                    darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900/50 to-gray-900' : 'bg-gradient-to-br from-white via-indigo-50/50 to-white'
                  }`}>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-110 shadow-lg ${themeClasses.accent} text-white inline-flex items-center gap-2`}
                    >
                      <ExternalLink size={18} />
                      View Project
                    </a>
                  </div>
                </div>
                
                <div className="p-7">
                  <h3 className={`text-xl font-bold mb-4 ${themeClasses.text} group-hover:text-gradient transition-all duration-300`}>{item.title}</h3>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {item.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className={`text-xs px-3 py-1.5 rounded-full font-semibold shadow-sm transition-all duration-300 hover:scale-105 ${themeClasses.tag}`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className={`mb-5 ${themeClasses.subtext} line-clamp-3 leading-relaxed`}>{item.description}</p>

                  <div className="flex justify-between items-center gap-3">
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex-1 px-4 py-2.5 rounded-lg ${themeClasses.projectBtn} text-white font-semibold text-center transition-all duration-300 hover:scale-105 shadow-md flex items-center justify-center gap-2`}
                    >
                      <ExternalLink size={16} />
                      <span>View Project</span>
                    </a>

                    {isAdmin && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => editPortfolio(item.id)}
                          className={`p-2.5 rounded-lg ${themeClasses.editBtn} text-white shadow-md transition-all duration-300 hover:scale-110`}
                          aria-label="Edit project"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => deletePortfolio(item.id)}
                          className={`p-2.5 rounded-lg ${themeClasses.deleteBtn} text-white shadow-md transition-all duration-300 hover:scale-110`}
                          aria-label="Delete project"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`p-16 text-center rounded-2xl ${themeClasses.card} shadow-2xl border fade-in-up ${darkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 mx-auto mb-6 text-indigo-400 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            <p className={`text-3xl font-bold mb-4 ${themeClasses.text}`}>No portfolio projects yet.</p>
            <p className={`text-lg mb-8 ${themeClasses.subtext}`}>Start showcasing your amazing work!</p>
            {isAdmin && (
              <button
                onClick={() => setActivePage('new-portfolio')}
                className={`px-8 py-4 rounded-xl ${themeClasses.accent} text-white font-semibold transition-all duration-300 shadow-xl hover:scale-105 hover-lift`}
              >
                Add Your First Project
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PortfolioPage;